import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA} from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, of } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { IExternalEntityCriteria } from 'src/app/masters/peoples/external-entity/external-entity.interface';
import { Api } from 'src/app/shared/constants/api';
import { DataService } from 'src/app/shared/services/data.service';

@Component({
    selector: 'app-previous-vessel-popup',
    templateUrl: './previous-vessel-popup.component.html',
    styleUrls: ['./previous-vessel-popup.component.less'],
    standalone: false
})
export class PreviousVesselPopupComponent implements OnInit {

  displayedColumns = ['vesselVoyage', 'consortium', 'line', 'weekNo', 'atdPol', 'routingId', 'transitTime'];
  dataSource = new MatTableDataSource<any>([]);
  // loading: boolean;
  entityCriteria: IExternalEntityCriteria = {};
  isDeleted:boolean = false;
  fetchPrevious:boolean = true;
  destinationName: string;
  lines: any[] = [];
  filteredLines = new BehaviorSubject<any[]>([]);
  consortiums: any[] = [];
  filteredConsortium = new BehaviorSubject<any[]>([]);
  noPreviousVessel: boolean = false;
  vesselVoyageFilter = new FormControl('');


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any, private formBuilder: FormBuilder ,private service: DataService, private logger: NGXLogger,
  ) {
    this.destinationName =this.data.destinationName;
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource([]);
     this.initializeVesselVoyageFilter();
    this.getConsortiumList();
    this.getLines();
    this.loadPreviousVessels();
  }

  private initializeVesselVoyageFilter(): void {
    this.vesselVoyageFilter.valueChanges
      .pipe(debounceTime(300))
      .subscribe(value => {
        this.applyVesselFilter(value);
      });
  }

  private applyVesselFilter(value: string): void {
    this.dataSource.filter = value ? value.trim().toLowerCase() : '';
  }

  loadPreviousVessels() {
    const criteria = {
      // weekNo : this.data.weekNo,
      fromDate: this.data.fromDate,
      // toDate: this.data.toDate,
      polId: this.data.polId,
      destinationId: this.data.destinationId,
      isDeleted: this.isDeleted,
      fetchPrevious: this.fetchPrevious,
    };
    this.logger.info("Criteria for the Load previous Vessel", criteria);

    this.service.get(Api.PREVIOUS_VESSEL_DETAILS, criteria).subscribe({
      next: (res: any) => {
        this.logger.info("Previous Vessel Details", res);
        if (!res) {
          this.noPreviousVessel = true;
          return;
        } else {
          this.noPreviousVessel = false;
        }

        // Extract RoutingIdIds (0 = Direct, -1 = TBA — neither is a real destination id)
        const routingIdIds = Array.from(
          new Set(
            res.flatMap((item: any) => item.planningDetails || [])
              .map((p: any) => Number(p.routingId))
              .filter(v => !isNaN(v) && v !== 0 && v !== -1)
          )
        );

        // Extract voyage IDs
        const voyageIds = Array.from(
          new Set(res.flatMap((item: any) => item.planningDetails || [])
            .filter((p: any) => p.destinationId === this.data.destinationId)
            .map((p: any) => p.vesselVoyageId)
            .filter(id => id != null)
          )
        );

        this.logger.info("vessel voyage ids", voyageIds);
        if (voyageIds.length === 0) {
          return;
        }

        // fetching all vessel voyage
        const voyageCriteria = { ids: voyageIds };
        this.service.get(Api.VESSEL_VOYAGES_BY_IDS, voyageCriteria).subscribe({
          next: (voyageDetails: any[]) => {
            this.logger.info("Voyage details", voyageDetails);
            this.fetchAllDestinations(routingIdIds).subscribe(destRes => {

              const destinationMap = new Map<number, string>();

              if (destRes?.contents?.length) {
                destRes.contents.forEach((d: any) => {
                  destinationMap.set(d.id, d.name);
                });
              }

              const enrichedRows = this.mergeVoyageData(res, voyageDetails, destinationMap);
              this.dataSource.data = enrichedRows;
            });
          },

          error: err => {
            console.error("Error fetching voyage details", err);
          }
        });
      },
      error: err => {
        console.error("Error loading previous vessels", err);
      }
    });
  }

private mergeVoyageData(weeklyData: any[], voyageDetails: any[], destinationMap: Map<number, string>) {
  const allMerged: any[] = [];

  weeklyData.forEach(weekItem => {
    (weekItem.planningDetails || [])
      .filter(plan => plan.destinationId === this.data.destinationId) //filtering only for the clicked destination
      .forEach(plan => {

        const voyage = voyageDetails.find(v => v.id === plan.vesselVoyageId);
        if (!voyage) return;

        let resolvedRoutingId = '';
        const routingIdNum = Number(plan.routingId);

        if (routingIdNum === 0) {
          resolvedRoutingId = 'DIRECT';
        } else if (routingIdNum === -1) {
          resolvedRoutingId = 'TBA';
        } else if (plan.routingId != null && !isNaN(routingIdNum)) {
          resolvedRoutingId = destinationMap.get(routingIdNum) || plan.routingId;
        }

        const row: any = {
          // Basic details from voyage
          vesselVoyage: voyage.vesselName,
          voyageNo: voyage.voyageNo,
          consortium: this.getConsortiumName(voyage.consortiumId),
          line: this.getLineName(voyage.lineId),

          atdPol: this.getAtdPol(
            voyage.vesselVoyagePortDetails,
            weekItem.polId
          ),
          transitTime: plan.transitTime,
          weekNo: weekItem.weekNo,
          routingId: resolvedRoutingId,
        };

        allMerged.push(row);
      });
  });

  return allMerged;
}

// Line
private getLines() {
    this.entityCriteria.entityBusinessType = "LINE";
    this.service.get(Api.EXTERNAL_ENTITY_FOR_DD, this.entityCriteria).subscribe(res => {
     if (res) {
      this.lines = res;
      this.logger.info('Line Master', this.lines);
      this.filteredLines.next(this.lines);
    }
    });
  }

private getLineName(lineId: number): string {
  const item = this.lines?.find(line => line.id === lineId);
  return item ? item.name : '---';
}

// Consortium
private getConsortiumList() {
  this.service.get(Api.CONSORTIUM).subscribe(res => {
    if (res) {
      this.consortiums = res;
      this.logger.info('Consortium Master', this.consortiums);
      this.filteredConsortium.next(this.consortiums);
    }
  });
}

private getConsortiumName(consortiumId: number): string {
  const item = this.consortiums?.find(c => c.id === consortiumId);
  return item ? item.name : '---';
}

private getAtdPol(portDetails: any[], polId: number) {
  if (!portDetails) return null;

  const polRecord = portDetails.find(p => p.portId === polId);
  return polRecord ? polRecord.asd : null;
}

private fetchAllDestinations(ids:any=[]) {
  if (!ids || ids.length === 0) return of([]);

  const criteria = { ids: ids };
  const pageableParams = {
    page: 0,
    size: ids.length
  };

  return this.service.get(
    Api.DESTINATION_NAMES_FOR_DD,
    { ...criteria, ...pageableParams }
  );
}


}
